import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { CalendarCheck, ArrowRight, Check, Plane, MapPin } from "lucide-react";

const SLIDES = [
  {
    src: "https://images.unsplash.com/photo-1691169555002-19e569af033b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    alt: "Lac de montagne au cœur des Rocheuses canadiennes",
    place: "Rocheuses, Canada",
    flag: "🇨🇦",
  },
  {
    src: "https://images.unsplash.com/photo-1717883560409-14adc1074f0e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    alt: "Harbour Bridge de Sydney au coucher du soleil",
    place: "Sydney, Australie",
    flag: "🇦🇺",
  },
  {
    src: "https://images.unsplash.com/photo-1583813611063-aa59412bbbcf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    alt: "Skyline de Toronto au crépuscule",
    place: "Toronto, Canada",
    flag: "🇨🇦",
  },
  {
    src: "https://images.unsplash.com/photo-1703850688116-700267cc9349?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    alt: "Littoral de la Great Ocean Road vu du ciel",
    place: "Great Ocean Road, Australie",
    flag: "🇦🇺",
  },
];

const BENEFITS = [
  "Inscriptions ouvertes — commencez dès aujourd'hui",
  "Consultation gratuite de 30 minutes avec un conseiller",
  "Choisissez et réglez vos packs en ligne, en toute sécurité",
  "Accompagnement complet pour le Canada et l'Australie",
];

const EASE = [0.22, 0.61, 0.36, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

export const AppointmentHero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.15]);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), 7000);
    return () => clearInterval(id);
  }, [reduce]);

  const slide = SLIDES[index];

  return (
    <section
      ref={ref}
      data-testid="appointment-hero"
      className="relative w-full overflow-hidden bg-navy"
    >
      {/* ————— Visuel cinématographique (fondu enchaîné + zoom très lent) ————— */}
      <motion.div style={reduce ? undefined : { y: bgY }} className="absolute inset-0 will-change-transform">
        <AnimatePresence initial={false}>
          <motion.img
            key={slide.src}
            src={slide.src}
            alt={slide.alt}
            loading="eager"
            decoding="async"
            initial={{ opacity: 0, scale: reduce ? 1 : 1.04 }}
            animate={{ opacity: 1, scale: reduce ? 1 : 1.15 }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 1.6, ease: "easeInOut" },
              scale: { duration: 11, ease: "linear" },
            }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
        {/* Préchargement discret des visuels suivants */}
        <div className="hidden">
          {SLIDES.map((s) => <img key={s.src} src={s.src} alt="" aria-hidden="true" />)}
        </div>
      </motion.div>

      {/* Voiles de lisibilité */}
      <div className="absolute inset-0 bg-navy/72" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/35" />
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[42rem] w-[42rem] rounded-full bg-gold/15 blur-[130px] animate-float-slow" />
      <div className="pointer-events-none absolute inset-x-3 top-3 bottom-3 border border-gold/20 sm:inset-x-6 sm:top-6 sm:bottom-6" />

      {/* Avion discret qui traverse le hero */}
      <div aria-hidden="true" className="pointer-events-none absolute left-0 top-10 w-full sm:top-14">
        <div className="animate-fly w-max">
          <Plane size={17} className="-rotate-[16deg] text-white/35" />
        </div>
      </div>

      {/* ————— Contenu ————— */}
      <motion.div
        style={reduce ? undefined : { y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto max-w-4xl px-5 py-20 text-center sm:px-8 sm:py-28"
      >
        <motion.div {...fadeUp(0)} className="flex items-center justify-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs text-white backdrop-blur-md sm:text-sm">🇦🇺 Australie</span>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs text-white backdrop-blur-md sm:text-sm">🇨🇦 Canada</span>
        </motion.div>

        <motion.h1
          {...fadeUp(0.12)}
          className="mt-6 font-heading text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          Réalisez votre projet au <span className="text-gold">Canada</span> ou en <span className="text-gold">Australie</span>
        </motion.h1>

        <motion.div {...fadeUp(0.24)} className="mx-auto mt-5 max-w-2xl space-y-4 text-base leading-relaxed text-white/85 sm:text-lg">
          <p>Work &amp; Travel Expert vous accompagne de A à Z : visa, préparation au départ et installation sur place.</p>
          <p>Prenez une consultation gratuite ou choisissez directement votre pack — tout se fait en ligne, en toute sécurité.</p>
        </motion.div>

        <motion.div {...fadeUp(0.36)} className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link
            to="/rendez-vous"
            data-testid="appointment-hero-cta"
            className="group inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-bold uppercase tracking-[0.08em] text-navy shadow-2xl shadow-gold/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover hover:shadow-gold/50 sm:px-11 sm:py-5 sm:text-base"
          >
            <CalendarCheck size={20} /> Prendre rendez-vous gratuitement
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1.5" />
          </Link>
          <Link
            to="/nos-packs"
            data-testid="appointment-hero-packs-cta"
            className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-8 py-4 text-sm font-semibold uppercase tracking-[0.08em] text-white transition-all duration-300 hover:bg-white hover:text-navy sm:px-10 sm:py-5 sm:text-base"
          >
            Découvrir nos packs <ArrowRight size={18} />
          </Link>
        </motion.div>

        <motion.ul
          {...fadeUp(0.48)}
          className="mx-auto mt-12 grid max-w-3xl gap-3.5 text-left sm:grid-cols-2"
          data-testid="appointment-hero-benefits"
        >
          {BENEFITS.map((b, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 + i * 0.1, ease: EASE }}
              className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.06] px-5 py-4 backdrop-blur-sm transition-colors duration-300 hover:border-gold/50 hover:bg-white/[0.09]"
            >
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold/15">
                <Check size={15} className="text-gold" />
              </span>
              <span className="text-sm leading-snug text-white/90 sm:text-base">{b}</span>
            </motion.li>
          ))}
        </motion.ul>
      </motion.div>

      {/* ————— Légende du visuel + indicateurs ————— */}
      <div className="relative z-10 mx-auto flex max-w-6xl flex-col items-center gap-4 px-5 pb-10 sm:flex-row sm:justify-between sm:px-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.place}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.7, ease: EASE }}
            data-testid="hero-slide-caption"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/70"
          >
            <MapPin size={13} className="text-gold" /> {slide.flag} {slide.place}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center gap-2.5" role="tablist" aria-label="Destinations">
          {SLIDES.map((s, i) => (
            <button
              key={s.src}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={s.place}
              onClick={() => setIndex(i)}
              data-testid={`hero-slide-dot-${i}`}
              className={`h-[3px] rounded-full transition-all duration-500 ${i === index ? "w-10 bg-gold" : "w-5 bg-white/30 hover:bg-white/60"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
